/**
 * The TelePortal situation answers — the vocabulary the setup step SENDS (F38).
 *
 * The relocation checklist attaches a template when its conditions pass
 * against the citizen's setup answers. Nothing tied the values a condition
 * named to the values a client could send, so three templates asked for
 * answers no screen offered: `salary_proof` wanted `intra_company`,
 * `proof_of_income` wanted `freelance` / `passive_income`, and
 * `credential_recognition` wanted a flag no app ever set. Proof of income
 * reached 3 of 25 production journeys.
 *
 * These constants are that tie. The capture widget builds its options FROM
 * them, and the catalog gate (`validateTelePortalCatalog`, run at brain boot
 * and by `npm run qa:teleportal-catalog`) refuses a condition that names a
 * value outside them — so a condition the UI can never satisfy fails the build
 * instead of silently removing a document from every kit.
 *
 * In `foundation` beside `VISA_PATHWAY_CODES` for the same reason (D-A2): the
 * brain's checklist engine, the API and both clients all read it, and
 * brain-service cannot import `@tonnie/locale`.
 */

/** `legalStatus.situation` — the broad legal situation. */
export const LEGAL_SITUATIONS = [
  'have_visa',
  'need_visa',
  'eu_citizen',
  'family_reunion',
  'digital_nomad',
  'company_sponsored',
  'student',
] as const;

export type LegalSituation = (typeof LEGAL_SITUATIONS)[number];

/** `professional.sponsorship_type` — the employment answer. */
export const EMPLOYMENT_SITUATIONS = [
  'company_sponsored',
  'employed_self',
  'self_employed',
  'entrepreneur',
  'student',
  'retired',
  'job_seeking',
] as const;

export type EmploymentSituation = (typeof EMPLOYMENT_SITUATIONS)[number];

/** `housing.plan` — the housing answer. */
export const HOUSING_PLANS = [
  'have_housing',
  'need_temp',
  'need_find',
  'company_provides',
] as const;

export type HousingPlan = (typeof HOUSING_PLANS)[number];

/**
 * Every answer path a catalog condition may name, with the values the setup
 * step can put there. `boolean` for a yes/no question; a list for a choice;
 * `'iso_country'` for a country code the SERVER supplies (`route.*` — the
 * journey's own route, never a client answer).
 *
 * A condition on any other path is a condition nothing can ever satisfy.
 */
export const EVALUABLE_SETUP_ANSWERS = {
  'household.has_partner': 'boolean',
  'household.has_children': 'boolean',
  'household.has_pets': 'boolean',
  'legalStatus.situation': LEGAL_SITUATIONS,
  /** Validated against the destination's offered `visa_types`, not a fixed list. */
  'legalStatus.visaPathway': 'visa_pathway',
  'professional.sponsorship_type': EMPLOYMENT_SITUATIONS,
  /** D-6 — "Do you work in a regulated profession in the country you are moving to?" */
  'professional.needs_credential_recognition': 'boolean',
  'housing.plan': HOUSING_PLANS,
  'route.origin': 'iso_country',
  'route.destination': 'iso_country',
} as const satisfies Record<string, 'boolean' | 'visa_pathway' | 'iso_country' | readonly string[]>;

export type EvaluableSetupAnswer = keyof typeof EVALUABLE_SETUP_ANSWERS;

export function isEvaluableSetupAnswer(field: string): field is EvaluableSetupAnswer {
  return Object.prototype.hasOwnProperty.call(EVALUABLE_SETUP_ANSWERS, field);
}
